'use client';

import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  setSelectedGeographies as setSelectedGeographiesAction,
  setSelectedGeographyObjects as setSelectedGeographyObjectsAction,
  clearSelectedGeographies as clearSelectedGeographiesAction,
  setTempSelectedGeographies as setTempSelectedGeographiesAction,
  setTempSelectedGeographyObjects as setTempSelectedGeographyObjectsAction,
  toggleTempGeography as toggleTempGeographyAction,
  toggleAllTempGeographies as toggleAllTempGeographiesAction,
  clearTempSelectedGeographies as clearTempSelectedGeographiesAction,
  initializeTempSelectionsFromCurrent as initializeTempSelectionsFromCurrentAction,
  applyTempGeographySelections as applyTempGeographySelectionsAction,
  setGeographySearch as setGeographySearchAction,
  setPage as setPageAction,
  setPageSize as setPageSizeAction,
  setSort as setSortAction,
  updateSort as updateSortAction,
  setSelectedInsight as setSelectedInsightAction,
  setVolumeTypes as setVolumeTypesAction,
  setModeType as setModeTypeAction,
  setPayerGroupBy as setPayerGroupByAction,
  setVolumeConfig as setVolumeConfigAction,
  updateVolumeConfig as updateVolumeConfigAction,
  removeVolumeConfig as removeVolumeConfigAction,
  initializeVolumeConfigs as initializeVolumeConfigsAction,
  clearVolumeConfigs as clearVolumeConfigsAction,
  setSelectedCodeSet as setSelectedCodeSetAction,
  clearSelectedCodeSet as clearSelectedCodeSetAction,
  setDateRange as setDateRangeAction,
  setGranularity as setGranularityAction,
  setTimePreset as setTimePresetAction,
  resetAnalytics as resetAnalyticsAction,
  selectSelectedGeographies,
  selectSelectedGeographyObjects,
  selectTempSelectedGeographies,
  selectTempSelectedGeographyObjects,
  selectGeographySearch,
  selectPage,
  selectPageSize,
  selectSort,
  selectSelectedInsight,
  selectVolumeTypes,
  selectModeType,
  selectPayerGroupBy,
  selectVolumeConfigs,
  selectSelectedCodeSet,
  selectDateRange,
  selectGranularity,
  selectPeriods,
  selectTimePreset,
  selectAnalyticsState,
  selectFormattedFiltersForAPI,
  selectPayerNamesFilterForAPI,
  AnalyticsSortState,
  GeographyObject,
  PayerGroupBy,
  VolumeConfig,
} from 'src/redux/slices/analyticsSlice';
import { ANALYTICS_LOCATION } from '../filters/constants';
import {
  Granularity,
  Period,
  TimePreset,
} from 'src/utils/quarterUtils';

/**
 * Custom hook to manage analytics state
 * Provides access to analytics selection state and actions
 * @param location - The analytics location (CODE_SETS or TRENDS)
 */
const useAnalytics = (location: string = ANALYTICS_LOCATION.TRENDS) => {
  const dispatch = useDispatch();

  // Shared selectors (not location-specific)
  const selectedGeographies = useSelector(selectSelectedGeographies);
  const selectedGeographyObjects = useSelector(selectSelectedGeographyObjects);
  const tempSelectedGeographies = useSelector(selectTempSelectedGeographies);
  const tempSelectedGeographyObjects = useSelector(
    selectTempSelectedGeographyObjects
  );
  const geographySearch = useSelector(selectGeographySearch);
  const selectedInsight = useSelector(selectSelectedInsight);
  const analyticsState = useSelector(selectAnalyticsState);
  const formattedFiltersForAPI = useSelector(selectFormattedFiltersForAPI);
  const payerNamesFilterForAPI = useSelector(selectPayerNamesFilterForAPI);

  // Location-specific selectors
  const page = useSelector(selectPage(location));
  const pageSize = useSelector(selectPageSize(location));
  const sort = useSelector(selectSort(location));
  const volumeTypes = useSelector(selectVolumeTypes(location));
  const modeType = useSelector(selectModeType(location));
  const payerGroupBy = useSelector(selectPayerGroupBy(location));
  const volumeConfigs = useSelector(selectVolumeConfigs(location));
  const selectedCodeSet = useSelector(selectSelectedCodeSet(location));
  const date_range = useSelector(selectDateRange(location));
  const granularity = useSelector(selectGranularity(location));
  const periods = useSelector(selectPeriods(location));
  const timePreset = useSelector(selectTimePreset(location));

  // Actions
  const setSelectedGeographies = useCallback(
    (geographies: number[]) => {
      dispatch(setSelectedGeographiesAction(geographies));
    },
    [dispatch]
  );

  const setSelectedGeographyObjects = useCallback(
    (objects: GeographyObject[]) => {
      dispatch(setSelectedGeographyObjectsAction(objects));
    },
    [dispatch]
  );

  const clearSelectedGeographies = useCallback(() => {
    dispatch(clearSelectedGeographiesAction());
  }, [dispatch]);

  const setTempSelectedGeographies = useCallback(
    (geographies: number[]) => {
      dispatch(setTempSelectedGeographiesAction(geographies));
    },
    [dispatch]
  );

  const setTempSelectedGeographyObjects = useCallback(
    (objects: GeographyObject[]) => {
      dispatch(setTempSelectedGeographyObjectsAction(objects));
    },
    [dispatch]
  );

  const toggleTempGeography = useCallback(
    (id: number, name: string, type: 'territory' | 'region') => {
      dispatch(toggleTempGeographyAction({ id, name, type }));
    },
    [dispatch]
  );

  const toggleAllTempGeographies = useCallback(
    (
      items: { id: number; name: string; type: 'territory' | 'region' }[],
      type: 'region' | 'territory'
    ) => {
      dispatch(toggleAllTempGeographiesAction({ items, type }));
    },
    [dispatch]
  );

  const clearTempSelectedGeographies = useCallback(() => {
    dispatch(clearTempSelectedGeographiesAction());
  }, [dispatch]);

  const initializeTempSelectionsFromCurrent = useCallback(() => {
    dispatch(initializeTempSelectionsFromCurrentAction());
  }, [dispatch]);

  const applyTempGeographySelections = useCallback(() => {
    dispatch(applyTempGeographySelectionsAction());
  }, [dispatch]);

  const setGeographySearch = useCallback(
    (search: string) => {
      dispatch(setGeographySearchAction(search));
    },
    [dispatch]
  );

  const setPage = useCallback(
    (page: number) => {
      dispatch(setPageAction({ page, location }));
    },
    [dispatch, location]
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch(setPageSizeAction({ pageSize, location }));
    },
    [dispatch, location]
  );

  const setSort = useCallback(
    (sort: AnalyticsSortState) => {
      dispatch(setSortAction({ sort, location }));
    },
    [dispatch, location]
  );

  const updateSort = useCallback(
    (sortUpdates: Partial<AnalyticsSortState>) => {
      dispatch(updateSortAction({ sortUpdates, location }));
    },
    [dispatch, location]
  );

  const setSelectedInsight = useCallback(
    (insight: string) => {
      dispatch(setSelectedInsightAction(insight));
    },
    [dispatch]
  );

  const setVolumeTypes = useCallback(
    (volumeTypes: ('claims' | 'patients' | 'providers' | 'sites')[]) => {
      dispatch(setVolumeTypesAction({ volumeTypes, location }));
    },
    [dispatch, location]
  );

  const setModeType = useCallback(
    (modeType: 'treating' | 'sending') => {
      dispatch(setModeTypeAction({ modeType, location }));
    },
    [dispatch, location]
  );

  const setPayerGroupBy = useCallback(
    (payerGroupBy: PayerGroupBy) => {
      dispatch(setPayerGroupByAction({ payerGroupBy, location }));
    },
    [dispatch, location]
  );

  const setVolumeConfig = useCallback(
    (codeSetId: string, config: VolumeConfig) => {
      dispatch(setVolumeConfigAction({ codeSetId, config, location }));
    },
    [dispatch, location]
  );

  const updateVolumeConfig = useCallback(
    (codeSetId: string, config: Partial<VolumeConfig>) => {
      dispatch(updateVolumeConfigAction({ codeSetId, config, location }));
    },
    [dispatch, location]
  );

  const removeVolumeConfig = useCallback(
    (codeSetId: string) => {
      dispatch(removeVolumeConfigAction({ codeSetId, location }));
    },
    [dispatch, location]
  );

  const initializeVolumeConfigs = useCallback(
    (codeSets: Array<{ id: number; label: string }>) => {
      dispatch(initializeVolumeConfigsAction({ codeSets, location }));
    },
    [dispatch, location]
  );

  const clearVolumeConfigs = useCallback(() => {
    dispatch(clearVolumeConfigsAction({ location }));
  }, [dispatch, location]);

  const getVolumeConfig = useCallback(
    (codeSetId: string) => {
      return volumeConfigs?.[codeSetId];
    },
    [volumeConfigs]
  );

  const setSelectedCodeSet = useCallback(
    (codeSet: any[]) => {
      dispatch(setSelectedCodeSetAction({ codeSet, location }));
    },
    [dispatch, location]
  );

  const clearSelectedCodeSet = useCallback(() => {
    dispatch(clearSelectedCodeSetAction({ location }));
  }, [dispatch, location]);

  const setDateRange = useCallback(
    (dateRange: { gte: string; lte: string }) => {
      dispatch(setDateRangeAction({ dateRange, location }));
    },
    [dispatch, location]
  );

  const setGranularity = useCallback(
    (granularityValue: Granularity) => {
      dispatch(setGranularityAction({ granularity: granularityValue, location }));
    },
    [dispatch, location]
  );

  const setTimePreset = useCallback(
    (
      preset: TimePreset,
      presetPeriods: Period[],
      dateRange?: { gte: string; lte: string }
    ) => {
      dispatch(
        setTimePresetAction({ preset, periods: presetPeriods, dateRange, location })
      );
    },
    [dispatch, location]
  );

  const resetAnalytics = useCallback(() => {
    dispatch(resetAnalyticsAction());
  }, [dispatch]);

  return {
    // State
    selectedGeographies,
    selectedGeographyObjects,
    tempSelectedGeographies,
    tempSelectedGeographyObjects,
    geographySearch,
    page,
    pageSize,
    sort,
    selectedInsight,
    volumeTypes,
    modeType,
    payerGroupBy,
    volumeConfigs,
    selectedCodeSet,
    date_range,
    granularity,
    periods,
    timePreset,
    analyticsState,
    formattedFiltersForAPI,
    payerNamesFilterForAPI,

    // Actions
    setSelectedGeographies,
    setSelectedGeographyObjects,
    clearSelectedGeographies,
    setTempSelectedGeographies,
    setTempSelectedGeographyObjects,
    toggleTempGeography,
    toggleAllTempGeographies,
    clearTempSelectedGeographies,
    initializeTempSelectionsFromCurrent,
    applyTempGeographySelections,
    setGeographySearch,
    setPage,
    setPageSize,
    setSort,
    updateSort,
    setSelectedInsight,
    setVolumeTypes,
    setModeType,
    setPayerGroupBy,
    setVolumeConfig,
    updateVolumeConfig,
    removeVolumeConfig,
    initializeVolumeConfigs,
    clearVolumeConfigs,
    getVolumeConfig,
    setSelectedCodeSet,
    clearSelectedCodeSet,
    setDateRange,
    setGranularity,
    setTimePreset,
    resetAnalytics,
  };
};

export default useAnalytics;
